import React from "react";
import { type IntegrationSimple, type CaseStudySimple } from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import { CenteredQuote } from "components/quotes";
import {
  SecurityFeatures,
  ComposableCdpAccordion,
  IndustryLeaderAwards,
  DestinationMarquee,
  CustomerStats,
  CaseStudySlider,
} from "components/content";
import { AidAccordion, ComposableCdpSlideshow, PlatformCards, UseCasesByTeam } from "components/home";
import { AuraPlate, SolidPlate } from "ui/plates";
import { DecorativeBackgroundImage } from "components/images";
import { VideoModal } from "ui/video";

const petsmartShort = "https://cdn.sanity.io/files/pwmfmi47/production/10756e213114aba50eb44269c201efc0be27f900.mp4";
const petsmartVideo = "https://cdn.sanity.io/files/pwmfmi47/production/d32cfe274623d8147658ece7363c7b838d00b374.mp4";
const petsmartCaptions = "/assets/home/petsmart-captions.vtt";
const petsmartThumbnail =
  "https://cdn.sanity.io/images/pwmfmi47/production/428b3051d614b60006c29dd8f70e3e59bc7df850-2108x1188.webp";

type HomeTemplateProps = {
  destinations: IntegrationSimple[];
  caseStudies: CaseStudySimple[];
};

export function HomeTemplate({ destinations, caseStudies }: HomeTemplateProps) {
  const petsmart = caseStudies.find((c) => c.customer.slug === "petsmart")!;
  const wmg = caseStudies.find((c) => c.customer.slug === "wmg")!;
  const weightWatchers = caseStudies.find((c) => c.customer.slug === "weight-watchers")!;
  const chime = caseStudies.find((c) => c.customer.slug === "chime")!;
  const whoop = caseStudies.find((c) => c.customer.slug === "whoop")!;

  return (
    <>
      <PlatformCards />

      <CustomerStats customers={[wmg.customer, petsmart.customer, weightWatchers.customer]} />

      <CaseStudySlider
        my={0}
        pb={sectionSpacingY}
        title="Leaders are re-inventing marketing with their data. You can too."
        slides={[
          {
            title: whoop.title,
            caseStudy: whoop,
            media: <DecorativeBackgroundImage src={whoop.fullImageUrl} />,
            url: `/customers/${whoop.customer.slug}`,
          },
          {
            title: "Warner Music Group leverages Hightouch to “super-serve” fans, artists, and labels",
            caseStudy: wmg,
            media: <DecorativeBackgroundImage src={wmg.fullImageUrl} />,
            url: `/customers/${wmg.customer.slug}`,
          },
          {
            title: "PetSmart personalizes journeys for over 70 million loyalty program members",
            caseStudy: petsmart,
            media: (
              <VideoModal
                shortSrc={petsmartShort}
                src={petsmartVideo}
                captions={petsmartCaptions}
                thumbnail={petsmartThumbnail}
                uploadDate="Feb 10, 2025"
                videoId="petsmart-case-study"
                title="PetSmart powers personalization for 70M+ loyalty members and drives double-digit growth"
                description="Bradley Breuer, VP of Marketing at PetSmart, discusses how the company is delivering personalized experiences to millions of pet-owners using Composable CDP powered by Hightouch."
              />
            ),
            url: `/customers/${petsmart.customer.slug}`,
          },
          {
            title: "WeightWatchers boosts new member growth by 52% and reduces data onboarding by 2 weeks",
            caseStudy: weightWatchers,
            media: <DecorativeBackgroundImage src={weightWatchers.fullImageUrl} />,
            url: `/customers/${weightWatchers.customer.slug}`,
          },
          {
            title: chime.title,
            caseStudy: chime,
            media: <DecorativeBackgroundImage src={chime.fullImageUrl} />,
            url: `/customers/${chime.customer.slug}`,
          },
        ]}
      />

      <SolidPlate
        bgColor="new.blue.950"
        spotlightColor="new.blue.900"
        theme="dark"
        maxW="100%"
        py={{ base: "bigger", md: "biggest" }}
        px={0}
      >
        <AidAccordion />

        <AuraPlate condensed theme="dark" variant="blue" my="biggest">
          <CenteredQuote
            theme="dark"
            maxW="800px"
            quoteText="With AI Decisioning, we saw a significant lift in our cross-sell campaigns within just 6 weeks. It’s transformed our team’s focus from manual tasks to high-impact strategic work"
            author={{ name: "Aoife O’Driscoll", job: "AVP Lifecycle Marketing" }}
            company={{ name: "Whoop", logo: whoop.customer.logoUrl }}
          />
        </AuraPlate>

        <ComposableCdpSlideshow />
      </SolidPlate>

      <Section my="0" pt={sectionSpacingY}>
        <ComposableCdpAccordion sectionTitle="Hightouch is an extension of your data warehouse" sectionMaxW="3xl" />
      </Section>

      <SecurityFeatures mt={{ base: "biggest", md: "huger" }} mb={{ base: "biggest", md: "huge" }} />

      <UseCasesByTeam />

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </>
  );
}
